import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff } from "lucide-react";
import AuthShell from "@/components/AuthShell";
import GoogleButton from "@/components/GoogleButton";
import { TwoFactorAuth } from "@/components/TwoFactorAuth";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/lib/i18n";

export default function Login() {
  const { t } = useI18n();
  const { loginWith } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [busy, setBusy] = useState(false);
  const [twofa, setTwofa] = useState(null);

  const finishLogin = (token, user) => {
    loginWith(token, user);
    toast.success("Welcome back!");
    navigate(user.role === "admin" ? "/admin" : "/dashboard");
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      if (data.twofa) {
        setTwofa({ pendingToken: data.pending_token, setupRequired: data.setup_required });
      } else {
        finishLogin(data.token, data.user);
      }
    } catch (err) {
      toast.error(apiError(err.response?.data?.detail) || err.message);
    } finally { setBusy(false); }
  };

  if (twofa) {
    return (
      <AuthShell title="Security check" subtitle={t("tagline")}
        footer={<button type="button" onClick={() => setTwofa(null)} className="text-orange-400 font-semibold" data-testid="twofa-back-link">Back to login</button>}>
        <TwoFactorAuth pendingToken={twofa.pendingToken} setupRequired={twofa.setupRequired} onSuccess={finishLogin} />
      </AuthShell>
    );
  }

  return (
    <AuthShell title={t("login")} subtitle={t("tagline")}
      footer={<>{t("no_account")} <Link to="/register" className="text-orange-400 font-semibold" data-testid="go-register-link">{t("create_account")}</Link></>}>
      <form onSubmit={submit} className="space-y-4">
        <div>
          <Label>{t("email")}</Label>
          <Input data-testid="login-email-input" type="text" value={email} onChange={(e) => setEmail(e.target.value)} required className="mt-1" placeholder="you@example.com" />
        </div>
        <div>
          <Label>{t("password")}</Label>
          <div className="relative mt-1">
            <Input data-testid="login-password-input" type={show ? "text" : "password"} value={password} onChange={(e) => setPassword(e.target.value)} required placeholder="••••••••" />
            <button type="button" onClick={() => setShow(!show)} data-testid="toggle-password-button" className="absolute end-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-orange-400">
              {show ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
            </button>
          </div>
        </div>
        <div className="text-end">
          <Link to="/forgot-password" className="text-sm text-gray-400 hover:text-orange-400" data-testid="forgot-password-link">{t("forgot_password")}</Link>
        </div>
        <Button type="submit" disabled={busy} data-testid="login-submit-button" className="w-full bg-orange-600 hover:bg-orange-500 btn-glow font-semibold py-6">
          {busy ? "..." : t("sign_in")}
        </Button>
      </form>
      <div className="flex items-center gap-3 my-5 text-gray-500 text-xs">
        <div className="flex-1 h-px bg-white/10" />{t("or")}<div className="flex-1 h-px bg-white/10" />
      </div>
      <GoogleButton />
    </AuthShell>
  );
}
